import { BackButton, PageSection, Text } from '@tectonicfi/tectonic-ui-kit';
import { TectonicAsset } from '@cronos-labs/tectonic-sdk/dist/types';
import { BigNumber } from 'ethers';
import { useRouter } from 'next/router';
import get from 'lodash/get';

import { TransactionModalsContext } from '@providers/TransactionModalsProvider';
import {
  getAvailableBorrow,
  getTotalBorrowLimit,
  getUnderlyingAmount,
  getUserBorrowBalance,
  getUtilizationRate,
} from '@lib/math';
import { onlyBorrowedAsset, onlySuppliedAsset } from '@lib/utils';
import SupportedAssetIcon from '@components/SupportedAssetIcon';
import TransactionModalsProvider from '@providers/TransactionModalsProvider';
import useAppSelector from '@hooks/useAppSelector';
import useUsdPrices from '@hooks/useUsdPrices';
import useUserMetrics from '@hooks/useUserMetrics';
import useWalletBalance from '@hooks/useWalletBalance';
import useWallets from '@hooks/useWallets';
import useSdkAndSupportedAssets from '@hooks/useSdkAndSupportedAssets';
import { MarketsSliceState } from '@redux/markets';

import { Mode } from '../MarketsPageView/types';

import BorrowCard from './BorrowCard';
import MarketDetailsCard from './MarketDetailsCard';
import SupplyCard from './SupplyCard';
import useBorrowCardData from './useBorrowCardData';
import useSupplyCardData from './useSupplyCardData';
import useYourBorrowCardData from './useYourBorrowCardData';
import useYourSupplyCardData from './useYourSupplyCardData';
import YourBorrowCard from './YourBorrowCard';
import YourSupplyCard from './YourSupplyCard';
import YourSupplyBorrowPlaceholderCard from './YourSupplyBorrowPlaceholderCard';
import InterestRateCard from './InterestRateCard';

const ARROW_TEXTS: Record<Mode, string> = {
  MAIN: 'Main Pool',
  DEFI: 'DeFi Pool',
  VENO: 'Veno Pool',
};

interface MarketDetailsPageViewProps {
  asset: TectonicAsset;
  mode: Mode;
}

function MarketDetailsPageView({
  asset,
  mode,
}: MarketDetailsPageViewProps): JSX.Element {
  const { back } = useRouter();

  const { borrowAmounts, supplyAmounts } = useAppSelector(
    (
      state
    ): {
      borrowAmounts: MarketsSliceState['borrowAmounts'];
      supplyAmounts: MarketsSliceState['supplyAmounts'];
    } => ({
      borrowAmounts: get(state, `${mode.toLowerCase()}Markets.borrowAmounts`),
      supplyAmounts: get(state, `${mode.toLowerCase()}Markets.supplyAmounts`),
    })
  );

  const { loaded: loadedUsdPrices, usdPrices } = useUsdPrices(mode);
  const { list: assets } = useSdkAndSupportedAssets(mode);
  const { currentAccount } = useWallets();
  const {
    data: userMetricsData,
    loaded: loadedUserMetrics,
    loading: loadingUserMetrics,
    refetch: refetchUserMetrics,
  } = useUserMetrics(mode);
  const {
    data: yourBorrowCardData,
    loading: loadingYourBorrowCardData,
    refetch: refetchYourBorrowCardData,
  } = useYourBorrowCardData(asset, mode);
  const {
    data: yourSupplyCardData,
    loading: loadingYourSupplyCardData,
    refetch: refetchYourSupplyCardData,
  } = useYourSupplyCardData(asset, mode);
  const { data: walletBalance, refetch: refetchWalletBalance } =
    useWalletBalance(mode, asset);
  const {
    data: borrowCardData,
    loading: loadingBorrowCardData,
    refetch: refetchBorrowCardData,
  } = useBorrowCardData(asset, mode);
  const {
    data: supplyCardData,
    loading: loadingSupplyCardData,
    refetch: refetchSupplyCardData,
  } = useSupplyCardData(asset, mode);
  const loadingInfo =
    loadingBorrowCardData ||
    loadingSupplyCardData ||
    loadingUserMetrics ||
    loadingYourBorrowCardData ||
    loadingYourSupplyCardData ||
    walletBalance.loading ||
    !loadedUsdPrices;
  const assetBorrowAmount = borrowAmounts[asset.symbol]
    ? BigNumber.from(borrowAmounts[asset.symbol])
    : null;
  const assetSupplyAmount = supplyAmounts[asset.symbol]
    ? BigNumber.from(supplyAmounts[asset.symbol])
    : null;
  const assetUsdPrice = usdPrices[asset.symbol]
    ? BigNumber.from(usdPrices[asset.symbol])
    : null;

  const borrowLimit =
    loadedUserMetrics && loadedUsdPrices
      ? getTotalBorrowLimit(
          assets.map((a) => {
            const collateralFactor =
              userMetricsData.collateralFactors[a.symbol] ?? 0;
            const isEnabledAsCollateral =
              userMetricsData.isCollateral[a.symbol] ?? false;
            const supplyAmount =
              userMetricsData.supplyAmounts[a.symbol] ?? BigNumber.from(0);
            const usdPrice = BigNumber.from(usdPrices[a.symbol]);

            return {
              asset: a,
              collateralFactor,
              isEnabledAsCollateral,
              supplyAmount,
              usdPrice,
            };
          })
        )
      : null;
  const borrowBalance =
    loadedUserMetrics && loadedUsdPrices
      ? getUserBorrowBalance(
          assets.map((a) => {
            const borrowAmount = userMetricsData.borrowAmounts[a.symbol];
            const usdPrice = BigNumber.from(usdPrices[a.symbol]);
            return { asset: a, borrowAmount, usdPrice };
          })
        )
      : null;
  const availableBorrowUsd =
    borrowLimit && borrowBalance
      ? getAvailableBorrow(borrowLimit, borrowBalance)
      : null;
  const availableBorrowAmount =
    availableBorrowUsd && assetUsdPrice
      ? getUnderlyingAmount(asset, assetUsdPrice, availableBorrowUsd)
      : null;
  const hasBorrowOnly =
    yourBorrowCardData.amount && yourSupplyCardData.amount && assetUsdPrice
      ? onlyBorrowedAsset({
          asset,
          assetUsdPrice,
          borrowAmount: yourBorrowCardData.amount,
          supplyAmount: yourSupplyCardData.amount,
        })
      : false;
  const hasSupplyOnly =
    yourBorrowCardData.amount && yourSupplyCardData.amount && assetUsdPrice
      ? onlySuppliedAsset({
          asset,
          assetUsdPrice,
          borrowAmount: yourBorrowCardData.amount,
          supplyAmount: yourSupplyCardData.amount,
        })
      : false;

  return (
    <TransactionModalsProvider
      onTxSuccess={(): void => {
        refetchBorrowCardData();
        refetchSupplyCardData();
        refetchUserMetrics();
        refetchWalletBalance();
        refetchYourBorrowCardData();
        refetchYourSupplyCardData();
      }}
    >
      <TransactionModalsContext.Consumer>
        {({ onOpenModal }) => (
          <PageSection className="pb-2" contentWrapperClassName="w-full">
            <BackButton onClick={back}>{ARROW_TEXTS[mode]}</BackButton>
            <div className="mb-4 flex items-center">
              <SupportedAssetIcon
                asset={asset}
                heightClassName="h-5"
                widthClassName="w-5"
              />
              <Text className="pl-2 text-heading1 font-bold mobile:text-heading2">{`${asset.name} (${asset.symbol})`}</Text>
            </div>
            {mode !== 'MAIN' && (
              <div className="pb-[26px] text-mediumSmall leading-[21px] text-white60">
                Note: You will be supplying or borrowing an asset that falls
                within an isolated pool. Such transactions take place in the
                isolated pool, separate from the protocol&apos;s aggregated
                asset pools. However, isolated pools are still susceptible to
                attacks or price inaccuracies. Read more&nbsp;
                <a
                  href="https://tectonic.gitbook.io/docs/protocol/isolated-pools"
                  rel="noopener noreferrer"
                  target="_blank"
                  className="text-yellow"
                >
                  here
                </a>
              </div>
            )}
            <div className="grid grid-cols-1 gap-y-2 gap-x-4 desktop:grid-cols-2 desktop:gap-y-4">
              {currentAccount ? (
                <>
                  <YourSupplyCard
                    mode={mode}
                    asset={asset}
                    loading={loadingInfo}
                    isEnabledAsCollateral={
                      userMetricsData.isCollateral[asset.symbol]
                    }
                    hasBorrowedAsset={
                      yourBorrowCardData.amount
                        ? yourBorrowCardData.amount.gt(BigNumber.from(0))
                        : null
                    }
                    hasBorrowedAssetOnly={hasBorrowOnly}
                    onAsCollateralClick={(): void =>
                      onOpenModal(asset, 'collateral')
                    }
                    onSupply={(): void => {
                      onOpenModal(asset, 'supply');
                    }}
                    onSwap={(): void => {
                      onOpenModal(asset, 'swap');
                    }}
                    onWithdraw={(): void => onOpenModal(asset, 'withdraw')}
                    usdPrice={assetUsdPrice}
                    walletBalance={walletBalance.balance}
                    walletBalanceAsset={walletBalance.asset}
                    {...yourSupplyCardData}
                  />
                  <YourBorrowCard
                    mode={mode}
                    asset={asset}
                    availableBorrowAmount={availableBorrowAmount}
                    borrowLimit={borrowLimit}
                    hasSuppliedAssetOnly={hasSupplyOnly}
                    loading={loadingInfo}
                    onBorrow={(): void => onOpenModal(asset, 'borrow')}
                    onShort={(): void => onOpenModal(asset, 'short')}
                    onRepay={(): void => {
                      onOpenModal(asset, 'repay');
                    }}
                    usdPrice={assetUsdPrice}
                    {...yourBorrowCardData}
                  />
                </>
              ) : (
                <YourSupplyBorrowPlaceholderCard />
              )}
              <SupplyCard
                mode={mode}
                asset={asset}
                amount={assetSupplyAmount}
              />
              <BorrowCard
                mode={mode}
                asset={asset}
                amount={assetBorrowAmount}
              />
              <InterestRateCard
                mode={mode}
                assetSymbol={asset.symbol}
                currentBorrowAPY={borrowCardData.apy}
                currentSupplyAPY={supplyCardData.apy}
                currentUtilizationRate={
                  assetBorrowAmount && assetSupplyAmount
                    ? getUtilizationRate(assetBorrowAmount, assetSupplyAmount)
                    : null
                }
              />
              <MarketDetailsCard
                asset={asset}
                assetBorrowAmount={assetBorrowAmount}
                assetSupplyAmount={assetSupplyAmount}
                mode={mode}
              />
            </div>
          </PageSection>
        )}
      </TransactionModalsContext.Consumer>
    </TransactionModalsProvider>
  );
}

export default MarketDetailsPageView;
